import * as _ from 'lodash';
import {
  ApplicationApi,
  ApplicationSession,
  ApplicationSessionUser,
  InstanceConfiguration,
  MabbleModule,
  MabbleModuleContentItem,
  MabbleModuleWidget,
  MabbleModuleWidgetContainer,
  MBL_KEY_MODULES, MBL_STATUS_OFF, MBL_STATUS_READY,
  MBL_TYPE_CONTAINER,
  MBL_TYPE_CONTENT,
  MBL_TYPE_INSTANCE,
  MBL_TYPE_MENU_ITEM,
  MBL_WIDGET_STYLE_A,
  MBL_WIDGET_STYLE_NAV_ITEM,
  MBL_WIDGET_TYPE_LINK,
  MBL_WIDGET_TYPE_WIDGET,
  MblObjectFormControl,
  uuidv4
} from '@core/models/application-session.model';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {of} from 'rxjs/observable/of';
import {environment} from '@environments/environment';

/**
 * mzs: 2017-11-14.120612
 * mzs: 2017-12-27.153858
 * mzs: 2018-01-05.070827
 * ...
 * */
export class SessionEvent {
  _onion?: number;
  activeRecord?: any;
  event: string;
  key: string;
  source: any;
  module?: MabbleModule<any>;

  constructor(options?: any) {
    this._onion = (this._onion || options && options._onion || 0) + 1;
    this.activeRecord = options && options.activeRecord;
    this.event = options && options.event;
    this.key = options && options.key;
    this.source = options && options.source;
    this.module = options && options.module;

    if (this.source && this.source._onion) {
      this._onion = this._onion + this.source._onion;
    }

  }


}

export class WatcherStore {
  private _map: [{ [p: string]: BehaviorSubject<any> }];

  constructor(root: object) {
    this._map = [{
      root: new BehaviorSubject(root)
    }];
  }

  next(key: string, value?: any): BehaviorSubject<any> {
    const reply = this.get(key);
    if (value) {
      reply.next(value);
    }
    return reply;
  }

  private get(key: string): BehaviorSubject<any> {
    const _m = this._map[key];
    if (_m) {
      return _m;
    }
    this._map[key] = new BehaviorSubject({});
    return this._map[key];
  }
}

@Injectable()
export class ApplicationSessionService {
  private _watchers: WatcherStore = null;

  private application: ApplicationSession;
  private anonymous: ApplicationSessionUser;

  private admin_roles: string[] = ['ROLE_ADMINISTRATOR', 'ROLE_SYS_ADMIN'];

  constructor() {

    // prepare a singleton watcher store
    this._watchers = new WatcherStore(new ApplicationSession({}));


    this.fetch('root-application-session.seed.json', (rootdata) => {
      this.fetch(environment.seed, (instancedata) => {

        const seed = _.merge({}, rootdata, instancedata, {
          enter_any_emergency_override_here: false
        });

        this.application = new ApplicationSession(seed);
        this.application.instance.state.next(this.seedInstanceConfiguration());

        // init session wide roles
        this.admin_roles = ['ROLE_ADMINISTRATOR', 'ROLE_SYS_ADMIN'];
        if (this.application.defaults.admin_roles) {
          this.application.defaults.admin_roles.forEach(ar => this.admin_roles.push(ar));
        }

        // publish the session
        this._watchers.next('root', this.application);
        this.fire();
      })
    });
  }

  get adminRoles(): string[] {
    return [...this.admin_roles];
  }

  /**
   * Best effort generate a display control set for any given model.
   * @param model to interrogate
   * @return [] of MabbleFormControl items.
   */
  public buildModelControls(model: any): MblObjectFormControl[] {
    if (model) {
      const results: Array<MblObjectFormControl> = [];
      Object.keys(model).forEach(k => {
        results.push(new MblObjectFormControl({
          model: model[k],
          name: model.name || model[k] || model._mblId,
          label: k,
          type: 'text',
          supportsHorizontal: true
        }));
      });
      return results;
    }
  }

  public fire() {
    this._watchers.next('menu', this.application.menu);
    this._watchers.next('session.api', this.application.api);
    this._watchers.next('user', this.application.user);
    this._watchers.next('user.data', this.application.user.data);

    this._watchers.next('root', this.application);
  }


  public set(key: string, value: any) {
    switch (key) {
      case('session.auth.token'): {
        this.application.user.auth_token = value;
        this._watchers.next('user', this.application.user);
        this._watchers.next('user.data', this.application.user.data);
        break;
      }
      case('session.user'): {
        this.application.user = value;
        this._watchers.next('user', this.application.user);
        this._watchers.next('user.data', this.application.user.data);
        break;
      }
      case ('user'):
      case ('user.data'):
      case ('session.user.data'): {
        this.application.user.data = value;
        this._watchers.next('user.data', this.application.user.data);
        break;
      }
      default: {
        this._watchers.next(key).getValue();
      }
    }
    this._watchers.next('root', this.application);
  }


  public get(key: string): any {
    switch (key) {
      case (''): {
        break;
      }
      case ('api'): {
        return this.application.api;
      }
      case ('apiHost'):
      case ('api.host'): {
        return this.application.api.host;
      }
      case ('user.data'): {
        return this.application.user.data;
      }
      case ('default.home.route'): {
        return this.application.defaults.home.route;
      }
      case ('session'): {
        return this.application;
      }
      case ('session.user'): {
        return this.application.user;
      }
      default: {
      }
    }
  }


  public observe(key: string): BehaviorSubject<ApplicationSession | ApplicationApi | ApplicationSessionUser | any> |
    Observable<ApplicationSession | ApplicationApi | ApplicationSessionUser | any> {

    if (!this._watchers || !this.application) {
      return undefined;
    }

    switch (key) {
      case ('api') :
      case ('session.api') : {
        return this._watchers.next(key, this.application.api);
      }
      case ('apiHost') :
      case ('api.host') : {
        return of(this.application.api.host);
      }
      case ('login.page') : {
        return of(this.application.login.page);
      }
      case ('menu') : {
        return this._watchers.next('menu', this.application.menu);
      }
      case ('is-authenticated') :
      case ('user') :
      case ('users') :
      case ('session.user') : {
        return this._watchers.next('user', this.application.user);
      }
      case ('session.user.data') :
      case ('user.data') : {
        if (this.application.user && this.application.user.data
          && this.application.user.auth_token && this.application.user.auth_token.length > 0) {
          return this._watchers.next('user.data', this.application.user.data);
        } else {
          return this._watchers.next('user.data', this.anonymous.data);
        }
      }
      case ('session') :
      default: {
        return this._watchers.next('root');
      }
    }

  }


  public subscribe(key: string): BehaviorSubject<ApplicationSession | ApplicationApi | ApplicationSessionUser | any> {
    switch (key) {
      case ('menu') : {
        return this._watchers.next('menu', this.application.menu);
      }
      case ('is-authenticated') :
      case ('user') :
      case ('users') :
      case ('session.user') : {
        return this._watchers.next('user', this.application.user);
      }
      case ('user.data') :
      case ('session.user.data') : {
        if (this.application.user && this.application.user.data
          && this.application.user.auth_token && this.application.user.auth_token.length > 0) {
          return this._watchers.next('user.data', this.application.user.data);
        } else {
          return this._watchers.next('user.data', this.anonymous.data);
        }
      }
      case ('session') :
      default: {
        return this._watchers.next('root', this.application);
      }
    }
  }


  private fetch(datkey: string, cb: any) {
    const req = new XMLHttpRequest();
    req.open('GET', 'assets/data/seed/' + datkey);
    req.onload = () => cb(JSON.parse(req.response));
    req.send();
  }


  private seedInstanceConfiguration(): InstanceConfiguration {
    const MBL_MODULE_KEY_MCIL_UUID = uuidv4();

    // top nav
    const home_link = new MabbleModuleWidget({
      name: 'Home Menu Item',
      description: 'A regular link (i.e href) to the home page',
      status: MBL_STATUS_READY,
      type: MBL_TYPE_MENU_ITEM,
      properties: {
        widget_route: '/',
        widget_title: 'Home',
        widget_style: MBL_WIDGET_STYLE_NAV_ITEM,
        widget_type: MBL_WIDGET_TYPE_LINK
      }
    });
    const configuration_link = new MabbleModuleWidget({
      name: 'Configuration Menu Item',
      description: 'A regular link (i.e href) to the configuration component page',
      status: MBL_STATUS_READY,
      type: MBL_TYPE_MENU_ITEM,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_route: '/app/sec/config/home',
        widget_title: 'Configuration',
        widget_style: MBL_WIDGET_STYLE_NAV_ITEM,
        widget_type: MBL_WIDGET_TYPE_LINK
      }
    });
    const logout_link = new MabbleModuleWidget({
      name: 'Log Out Menu Item',
      description: 'A regular link (i.e href) performing the user logout page',
      status: MBL_STATUS_READY,
      type: MBL_TYPE_MENU_ITEM,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_USER']
      },
      properties: {
        widget_route: '/app/authentication/signout',
        widget_title: 'Log Out',
        widget_style: MBL_WIDGET_STYLE_NAV_ITEM,
        widget_type: MBL_WIDGET_TYPE_LINK
      }
    });
    const login_link = new MabbleModuleWidget({
      name: 'Log In Menu Item',
      description: 'A regular link (i.e href) performing the user login page',
      status: MBL_STATUS_READY,
      type: MBL_TYPE_MENU_ITEM,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_USER']
      },
      properties: {
        widget_route: '/app/authentication/signin',
        widget_title: 'Log In',
        widget_style: MBL_WIDGET_STYLE_NAV_ITEM,
        widget_type: MBL_WIDGET_TYPE_LINK
      }
    });


    const dev_spike_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_classes: 'fa fa-bus',
        widget_color: 'yellow',
        widget_title: 'Spike: Drag and Drop',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/spike',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Test and validate functional elements',
        widget_width: '4'
      }
    });
    const dev_spike_widget_form_capture = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'orange',
        widget_title: 'Functional Spike',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/form/capture/list/d0a46df7-1135-41b9-adcb-02898b82a69d',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Test and validate functional elements',
        widget_width: '4'
      }
    });


    const gm_admin_console_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_classes: 'icon-basic-headset',
        widget_color: 'orange',
        widget_title: 'GM Admin Console',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/admin-console',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Grant Management Tools',
        widget_width: '7'
      }
    });
    const gm_application_widget = new MabbleModuleWidget({
      name: 'Grant Management: Applications',
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR', 'ROLE_APPLICANT']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'brown',
        widget_title: 'Grant Application',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/app.workflow.grant.application.csu',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Grant Management: Application collections',
        widget_width: '4'
      }
    });
    const gm_coordinators_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR', 'ROLE_GRANT_MANAGER']
      },
      properties: {
        widget_classes: 'icon-basic-book-pencil',
        widget_color: 'yellow',
        widget_title: 'Coordinator Management',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/coordinators',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'coordinator role management',
        widget_width: '4'
      }
    });
    const gm_review_dashboard_widget = new MabbleModuleWidget({
      name: 'Grant Management',
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR', 'ROLE_GRANT_MANAGER', 'ROLE_GRANT_COORDINATOR']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'red',
        widget_title: 'Grant Management: Dashboard',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/grant-management/home',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Grant Management: Application Dashboard',
        widget_width: '4'
      }
    });


    const kbms_doclib = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'orange',
        widget_title: 'Documents',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/form/capture/list/d0a46df7-1135-41b9-adcb-02898b82a69d',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'KBMS Document Library',
        widget_width: '4'
      }
    });
    const kbms_search = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_classes: 'fa fa-search',
        widget_color: 'light-blue',
        widget_title: 'Search',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/kb-search/search',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Knowledgebase Search',
        widget_width: '4'
      }
    });


    const mbl_codes_file_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'purple',
        widget_title: 'Codes File',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/lookup/home',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Lookup and Code meta-data',
        widget_width: '6'
      }
    });
    const mbl_content_manager = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR', 'ROLE_GRANT_MANAGER']
      },
      properties: {
        widget_classes: 'icon-basic-book-pencil',
        widget_color: 'light-blue',
        widget_title: 'Content Manager',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/content/manager/home',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'content management system',
        widget_width: '4'
      }
    });
    const mbl_data_loader_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR']
      },
      properties: {
        widget_classes: 'icon-arrows-drag-right-dashed',
        widget_color: 'orange',
        widget_title: 'Data Loader',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/data-loader/home',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'data loader',
        widget_width: '4'
      }
    });
    const mbl_forms_dashboard_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR']
      },
      properties: {
        widget_classes: 'icon-basic-lightbulb',
        widget_color: 'green',
        widget_title: 'Form Builder Dashboard',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/form/dashboard',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'form builder',
        widget_width: '4'
      }
    });


    const sap_data_migration_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN']
      },
      properties: {
        widget_classes: 'icon-arrows-circle-minus',
        widget_color: 'yellow',
        widget_title: 'SAP Data Migration Runner',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/dm-sap-runner',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'data migrate - form capture 2017-11-02',
        widget_width: '4'
      }
    });


    const sat_assessment_builder_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR']
      },
      properties: {
        widget_classes: 'icon-basic-star',
        widget_color: 'grey',
        widget_title: 'Assessment Builder',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/assessments/definition/home',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'assessment definition manager',
        widget_width: '4'
      }
    });
    const sat_assessment_campaign_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR']
      },
      properties: {
        widget_classes: 'icon-basic-clessidre',
        widget_color: 'pink',
        widget_title: 'Assessment Campaigns',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/assessments/campaign/home',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'assessment campaign dashboard',
        widget_width: '4'
      }
    });
    const sat_assessment_capture_widget = new MabbleModuleWidget({
      name: 'Self Assessment Workflow',
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR', 'ROLE_PARTICIPANT']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'brown',
        widget_title: 'Self Assessment',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/app.workflow.tiger',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Self Assessment Questionnaire',
        widget_width: '4'
      }
    });
    const sat_participant_landing_widget = new MabbleModuleWidget({
      name: 'Participant Candidate Landing',
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_USER', 'ROLE_PARTICIPANT', 'ROLE_APPLICANT']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'green',
        widget_title: 'Your Dashboard',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/participant/sat/home',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'Participant Dashboard',
        widget_width: '3'
      }
    });


    const tgr_wflow_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_USER', 'ROLE_GRANT_APPLICANT']
      },
      properties: {
        widget_classes: 'icon-basic-keyboard',
        widget_color: 'orange',
        widget_title: 'TB SED Grant Application',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/app.workflow.tiger',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'TBSED Apr/May 2018 Assessment Application Window',
        widget_width: '4'
      }
    });


    const user_admin_widget = new MabbleModuleWidget({
      status: MBL_STATUS_READY,
      type: MBL_WIDGET_TYPE_WIDGET,
      accessControl: {
        roles: ['ROLE_SYS_ADMIN', 'ROLE_ADMINISTRATOR']
      },
      properties: {
        widget_classes: 'fa fa-address-card-o',
        widget_color: 'blue',
        widget_title: 'Users',
        widget_style: MBL_WIDGET_STYLE_A,
        widget_route: '/app/sec/users/list',
        widget_type: MBL_WIDGET_TYPE_LINK,
        widget_text: 'User Administration',
        widget_width: '4'
      }
    });


    // modules:
    const sec_instance_top_nav = {
      dev: [
        home_link,
        configuration_link,
        login_link
      ],
      sap: [
        home_link,
        logout_link
      ],
      satrust: [],
      sus: [
        home_link,
        logout_link
      ],
      tgr: [
        home_link,
        logout_link
      ],
      usc: [],
    };
    const sec_dashboard_widgets = {
      dev: [
        dev_spike_widget,
        gm_admin_console_widget,
        gm_application_widget,
        gm_coordinators_widget,
        gm_review_dashboard_widget,
        kbms_doclib,
        kbms_search,
        mbl_codes_file_widget,
        mbl_content_manager,
        mbl_data_loader_widget,
        mbl_forms_dashboard_widget,
        sat_assessment_builder_widget,
        sat_assessment_campaign_widget,
        sat_assessment_capture_widget,
        user_admin_widget
      ],
      sap: [
        mbl_codes_file_widget,
        mbl_data_loader_widget,
        mbl_forms_dashboard_widget,
        sap_data_migration_widget
      ],
      satrust: [
        kbms_doclib,
        kbms_search,
        mbl_codes_file_widget,
        mbl_forms_dashboard_widget
      ],
      sus: [
        mbl_codes_file_widget,
        mbl_content_manager,
        mbl_forms_dashboard_widget,
        sat_assessment_builder_widget,
        sat_assessment_campaign_widget,
      ],
      tgr: [
        gm_admin_console_widget,
        gm_review_dashboard_widget,
        mbl_codes_file_widget,
        mbl_content_manager,
        mbl_forms_dashboard_widget,
        sat_assessment_builder_widget,
        sat_assessment_capture_widget,
      ],
      usc: [
        gm_admin_console_widget,
        gm_application_widget,
        gm_coordinators_widget,
        gm_review_dashboard_widget,
        mbl_codes_file_widget,
        mbl_forms_dashboard_widget,
      ]
    };
    const secuser_dashboard_widgets = {
      dev: [],
      sap: [
        mbl_codes_file_widget,
        mbl_data_loader_widget,
        mbl_forms_dashboard_widget,
        sap_data_migration_widget
      ],
      satrust: [
        kbms_doclib,
        kbms_search,
      ],
      sus: [
        sat_participant_landing_widget
      ],
      tgr: [
        tgr_wflow_widget
      ],
      usc: [
        gm_review_dashboard_widget
      ]
    };

    const pub = new InstanceConfiguration({
      properties: {
        instance_brand_banner_img: this.application.defaults.home.banner,
        instance_brand_banner_visible: true,
        instance_brand_logo_img: this.application.defaults.home.logo,
        instance_main: new MabbleModuleContentItem({
          name: 'Public Landing Page Content',
          description: 'The general public facing content used on the welcome page.',
          status: MBL_STATUS_READY,
          type: MBL_TYPE_CONTENT,
          context: this.application.instance.homeContentKey
        }),
        instance_top_nav: [home_link, login_link],
      }
    });
    const sec = new InstanceConfiguration({
      properties: {
        instance_brand_banner_visible: !this.application.user || !this.application.user.isAuthenticated(),
        instance_left_sidebar_visible: false,
        instance_main: new MabbleModuleWidgetContainer({
          name: 'Admin Landing Page Content',
          description: 'The content used on the administration dashboard.',
          status: MBL_STATUS_READY,
          type: MBL_TYPE_CONTAINER,
          contains: sec_dashboard_widgets[this.application.ssitk]
        }),
        instance_top_nav: sec_instance_top_nav[this.application.ssitk] ? sec_instance_top_nav[this.application.ssitk] : [],
      }
    });
    const secuser = new InstanceConfiguration({
      properties: {
        instance_brand_banner_visible: !this.application.user || !this.application.user.isAuthenticated(),
        instance_left_sidebar_visible: false,
        instance_main: new MabbleModuleWidgetContainer({
          name: 'User Landing Page Content',
          description: 'The content used on the user home.',
          status: MBL_STATUS_READY,
          type: MBL_TYPE_CONTAINER,
          contains: secuser_dashboard_widgets[this.application.ssitk]
        }),
        instance_top_nav: sec_instance_top_nav[this.application.ssitk] ? sec_instance_top_nav[this.application.ssitk] : [],
      }
    });

    return new InstanceConfiguration({
      _mblId: MBL_MODULE_KEY_MCIL_UUID,
      type: MBL_TYPE_INSTANCE,
      context: MBL_KEY_MODULES,
      identifier: 1,
      name: 'Instance Configuration',
      description: 'Application module configuration for landing page bootstrap.',
      status: MBL_STATUS_READY,
      properties: {},
      initialStates: [
        {key: 'pub', state: pub},
        {key: 'sec', state: sec},
        {key: 'secuser', state: secuser}
      ]
    });

  }


}



// WEBPACK FOOTER //
// ./src/app/core/services/application-session.service.ts